import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useLayoutEffect,
} from "react";
import { useNavigate } from "react-router-dom";
import { formatErrors } from "utils/api/endpoints";
import { Tooltip } from "app/components/parts/tooltip";
import { ActivationCodeCongratulationsDialog } from "../../components/ActivationCongratulations";
import { useActivationSlice } from "app/pages/Customer/Activation/slice";
import {
  getActivationFlowData,
  saveActivationFlowData,
} from "app/pages/Customer/FixedPhone/Checkout/utils/fixed-phone-storage";
import { useActivationCodeRateLimit } from "../hooks/use-activation-code-rate-limit";
import {
  formatCooldownSeconds,
  shouldCountActivationVerifyThrownFailure,
} from "../utils/activation-code-attempt-storage";

import { Input } from "app/components/ui/input";
import { Button } from "app/components/ui/button";
import { Icons } from "app/components/ui/icons";
import { useToast } from "app/components/ui/use-toast";

const ACTIVATION_CODE_MAX_LENGTH = 24;

function sanitizeActivationValue(raw: string): string {
  let out = "";
  for (const ch of raw) {
    if (/[A-Za-z0-9]/.test(ch)) {
      out += ch.toUpperCase();
    } else if (ch === " ") {
      out += ch;
    }
  }
  return out.slice(0, ACTIVATION_CODE_MAX_LENGTH);
}

function caretAfterSanitize(raw: string, rawIndex: number): number {
  let pos = 0;
  for (let i = 0; i < rawIndex && i < raw.length; i++) {
    const ch = raw[i];
    if (/[A-Za-z0-9]/.test(ch) || ch === " ") pos++;
  }
  return pos;
}

export const ActivationCode = () => {
  const { toast } = useToast();
  const rateLimitScope =
    getActivationFlowData()?.zipCode?.trim() ||
    "activation-code-verification";
  const {
    isBlocked,
    inCooldown,
    failures,
    attemptsRemaining,
    remainingSeconds,
    registerFailure,
    registerSuccess,
  } = useActivationCodeRateLimit(rateLimitScope);

  const { useGetDeviceDetailsMutation } = useActivationSlice();
  const [getDeviceDetails, { isLoading: isVerifying }] =
    useGetDeviceDetailsMutation();
  const [showCongratulationsDialog, setShowCongratulationsDialog] =
    useState(false);
  const [activationCode, setActivationCode] = useState(() =>
    sanitizeActivationValue(
      String(getActivationFlowData()?.activationCode || ""),
    ),
  );
  const [simData, setSimData] = useState<any>(null);

  const navigate = useNavigate();
  /** Extra guard beyond disabled={isVerifying} for slow networks / double taps. */
  const verifyInFlightRef = useRef(false);
  const activationInputRef = useRef<HTMLInputElement>(null);
  const pendingSelectionRef = useRef<{ start: number; end: number } | null>(
    null,
  );

  const handleActivationCodeChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const input = e.currentTarget;
    const raw = input.value;
    const start = input.selectionStart ?? 0;
    const end = input.selectionEnd ?? 0;
    const next = sanitizeActivationValue(raw);
    const maxPos = next.length;
    pendingSelectionRef.current = {
      start: Math.min(caretAfterSanitize(raw, start), maxPos),
      end: Math.min(caretAfterSanitize(raw, end), maxPos),
    };
    setActivationCode(next);
  };

  useLayoutEffect(() => {
    const pending = pendingSelectionRef.current;
    const el = activationInputRef.current;
    if (!pending || !el) return;
    pendingSelectionRef.current = null;
    try {
      el.setSelectionRange(pending.start, pending.end);
    } catch {
      // Some input types / states reject setSelectionRange
    }
  }, [activationCode]);

  // Route protection: Check for zip code verification
  useEffect(() => {
    const activationZipCode = getActivationFlowData()?.zipCode;
    if (!activationZipCode) {
      navigate("/activation");
    }
  }, [navigate]);

  // Persist typed activation code so the field can be restored in-session.
  useEffect(() => {
    saveActivationFlowData({ activationCode });
  }, [activationCode]);

  const handleVerify = useCallback(async () => {
    if (!activationCode.trim() || isBlocked) return;
    if (verifyInFlightRef.current || isVerifying) return;
    verifyInFlightRef.current = true;
    try {
      const response = await getDeviceDetails({
        activationCode: activationCode,
      }).unwrap();
      const verifiedDevice = response?.data?.device;
      if (verifiedDevice) {
        registerSuccess();
        setSimData(response.data);
        // Fresh verification can switch monthly/yearly; clear downstream selections to avoid stale routing.
        saveActivationFlowData({
          activationCode,
          simData: response.data,
          monthlyPlanId: undefined,
          yearlyPlanId: undefined,
          planId: undefined,
          selectedPlanType: undefined,
          optForSecondMonthUpsell: undefined,
          optForSecurityPlan: undefined,
          securityPlanId: undefined,
        });
        setShowCongratulationsDialog(true);
      } else {
        registerFailure();
        toast({
          title: "Error",
          description:
            response?.data?.message ||
            "Validation failed. Please check your code.",
          variant: "error",
        });
      }
    } catch (error: any) {
      if (shouldCountActivationVerifyThrownFailure(error)) {
        registerFailure();
      }
      toast({
        title: "Error",
        description: typeof error === "string" ? error : formatErrors(error),
        variant: "error",
      });
    } finally {
      verifyInFlightRef.current = false;
    }
  }, [
    activationCode,
    getDeviceDetails,
    isBlocked,
    isVerifying,
    registerFailure,
    registerSuccess,
    toast,
  ]);

  return (
    <React.Fragment>
      <div className="w-full max-w-[27.5rem] md:max-w-[33.5rem]">
        <form className="w-full bg-white p-4 sm:p-5 lg:p-6 lg:pt-5 border border-[#E6E7EB] rounded-lg md:rounded-xl">
          <div className="size-9 sm:size-10 md:size-11 lg:size-[3.125rem] mb-2 sm:mb-1.5">
            <img
              src="/assets/phone-office.svg"
              className="size-full"
              alt="Phone Office"
            />
          </div>
          <h3 className="font-mobile text-[#121212] text-lg sm:text-xl md:text-[1.375rem] lg:text-2xl font-extrabold leading-[1.375rem] sm:leading-6 md:leading-[1.625rem] lg:leading-7 tracking-[-0.045rem] sm:tracking-normal mb-1 sm:mb-1.5 lg:mb-2">
            Activation Code
          </h3>
          <p className="text-black text-[0.825rem] sm:text-sm font-medium leading-[1.125rem] sm:leading-5 mb-5 sm:mb-[1.125rem] lg:mb-[0.94rem]">
            {/* You can find the IMEI/MEID/Serial Number on the Activation code that
            came with the device. */}
            Enter the IMEI, SIM Card Number or Activation Code from your
            included activation card.
          </p>
          <div
            className={`relative ${
              inCooldown || failures > 0
                ? "mb-1 sm:mb-1.5"
                : "mb-4 sm:mb-5 lg:mb-6"
            }`}
          >
            <Input
              ref={activationInputRef}
              id="activation-code"
              type="text"
              value={activationCode}
              className="bg-white h-10 md:h-[2.875rem] p-2.5 pr-8 md:p-[0.78125rem] md:pr-9 md:pb-[0.8125rem] border-[#D1D5DB] rounded-xl text-black text-[0.825rem] sm:text-sm font-medium leading-4 md:leading-5 focus-visible:outline focus:outline-[#707C8F]"
              onChange={handleActivationCodeChange}
              maxLength={ACTIVATION_CODE_MAX_LENGTH}
              placeholder="Enter Activation Code"
              disabled={isBlocked}
              aria-disabled={isBlocked}
            />
            <div className="pointer-events-auto absolute right-2.5 top-1/2 z-20 -translate-y-1/2 opacity-100 md:right-[0.78125rem]">
              <Tooltip
                content="The IMEI and Activation Code are printed on the card included in your device box. You can also find the IMEI on the back of your device."
                side="bottom"
              />
            </div>
          </div>
          {(inCooldown || failures > 0) && (
            <p
              className={`text-[0.825rem] sm:text-sm font-medium leading-[1.125rem] sm:leading-5 mb-3.5 sm:mb-4 ${
                inCooldown ? "text-[#991B1B]" : "text-[#92400E]"
              }`}
              role="status"
              aria-live={inCooldown ? "polite" : undefined}
            >
              {inCooldown ? (
                <>
                  Too many failed attempts. For your security, this form is locked. Please try again in{" "}
                  <span className="tabular-nums font-semibold">
                    {formatCooldownSeconds(remainingSeconds)}
                  </span>
                  .
                </>
              ) : attemptsRemaining === 1 ? (
                "Invalid entry. 1 attempt remaining."
              ) : (
                `Invalid entry. ${attemptsRemaining} attempts remaining.`
              )}
            </p>
          )}
          <Button
            type="button"
            variant="primary"
            disabled={isVerifying || isBlocked}
            onClick={handleVerify}
          >
            {isVerifying ? (
              <div className="flex items-center gap-0.5 sm:gap-1">
                <Icons.Spinner className="w-4 h-4 animate-spin md:w-5 md:h-5" />
                <span>Verifying...</span>
              </div>
            ) : (
              "Verify"
            )}
          </Button>
        </form>
      </div>
      <ActivationCodeCongratulationsDialog
        showDialog={showCongratulationsDialog}
        setShowDialog={setShowCongratulationsDialog}
        data={simData}
      />
    </React.Fragment>
  );
};
